// frontend/src/components/NotificationCenter.jsx - FIXED
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Trash2, X } from 'lucide-react';
import api from '../services/api';
import socketService from '../services/socket';
import { useAuth } from '../contexts/AuthContext';

const NotificationCenter = () => {
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPanel, setShowPanel] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadNotifications = useCallback(async () => {
    if (isLoading || !user) return;

    try {
      setLoading(true);
      const res = await api.get('/notifications');
      setNotifications(res.data.notifications || res.data.data.notifications || []);
      setUnreadCount(res.data.unreadCount || res.data.data.unreadCount || 0);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [user, isLoading]);

  const loadUnreadCount = useCallback(async () => {
    if (isLoading || !user) return;

    try {
      const res = await api.get('/notifications/unread/count');
      setUnreadCount(res.data.count || res.data.data.count || 0);
    } catch (err) {
      console.error('Failed to load unread count:', err);
    }
  }, [user, isLoading]);

  useEffect(() => {
    if (user && !isLoading) {
      loadNotifications();
      loadUnreadCount();

      // ✅ CRITICAL FIX: Safe socket listener registration
      const handleNewNotification = (notification) => {
        setNotifications(prev => [notification, ...prev]);
        setUnreadCount(prev => prev + 1);
      };

      // Only register if socket is connected
      if (socketService.isSocketConnected && socketService.isSocketConnected()) {
        try {
          socketService.safeOn('notification', handleNewNotification);
        } catch (error) {
          console.error('Error registering notification listener:', error);
        }
      }

      return () => {
        // Clean up socket listener
        try {
          if (socketService.socket && typeof socketService.socket.off === 'function') {
            socketService.socket.off('notification', handleNewNotification);
          }
        } catch (error) {
          console.error('Error cleaning up notification listener:', error);
        }
      };
    }
  }, [user, isLoading, loadNotifications, loadUnreadCount]);

  const markAsRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications(prev =>
        prev.map(n => ((n.id || n._id) === id ? { ...n, isRead: true } : n))
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.patch('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const deleteNotification = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(n => (n.id || n._id) !== id));
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) markAsRead(notification.id || notification._id);

    if (notification.data?.orderId) {
      navigate(`/dashboard/orders/${notification.data.orderId}`);
    } else if (notification.data?.gigId) {
      navigate(`/gigs/${notification.data.gigId}`);
    } else if (notification.data?.url) {
      navigate(notification.data.url);
    }

    setShowPanel(false);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_order': return '🛒';
      case 'order_completed': return '✅';
      case 'payment_received': return '💰';
      case 'system_alert': return '⚠️';
      default: return '🔔';
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowPanel(!showPanel)}
        className="relative p-2 hover:bg-gray-100 rounded-full"
      >
        <Bell className="w-6 h-6 text-gray-700" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showPanel && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowPanel(false)} />
          <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border z-50 max-h-[600px] flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Notifications</h3>
              <div className="flex items-center space-x-2">
                {unreadCount > 0 && (
                  <button onClick={markAllAsRead} className="text-xs text-blue-600 hover:text-blue-700">
                    <CheckCheck className="w-4 h-4" />
                  </button>
                )}
                <button onClick={() => setShowPanel(false)} className="text-gray-500 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto flex-1">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Bell className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No notifications</p>
                </div>
              ) : (
                notifications.map((notification) => (
                  <div
                    key={notification.id || notification._id}
                    className={`p-4 border-b hover:bg-gray-50 cursor-pointer ${!notification.isRead ? 'bg-blue-50' : ''
                      }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-xl">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm">{notification.title}</p>
                        <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(notification.createdAt).toLocaleString()}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notification.id || notification._id);
                        }}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {notifications.length > 0 && (
              <div className="p-3 border-t">
                <button
                  onClick={() => {
                    navigate('/dashboard/notifications');
                    setShowPanel(false);
                  }}
                  className="w-full text-center text-sm text-blue-600 hover:text-blue-700"
                >
                  View all notifications
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationCenter;